import { DownOutlined, PlayCircleOutlined } from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { CONSTANT } from '@salescore/client-base'
import { checkSyncEnabled, useMe } from '@salescore/client-common'
import type { CompiledSheetViewConfig, ViewQueryList } from '@salescore/core'
import { ButtonWithTooltip } from '@salescore/frontend-common'
import { App, Button, Drawer, Dropdown, type MenuProps, Popconfirm, Space } from 'antd'
import { isNotNullish } from 'effect/Predicate'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilState, useRecoilValue } from 'recoil'

import { partitionPickedRecordIds } from '../../../domain/partitionPickedRecordIds'
import { pickedIdsAtom } from '../../../recoil/records/atoms'
import { useIsSavingState } from '../../../recoil/records/hooks'
import { useRecordsAndChangesMutation } from '../../../recoil/records/mutations/recordsAndChangesMutation'
import { type SalescoreObjectAbility, type ViewAbility, viewsContextAtom } from '../../../recoil/view/atoms'
import { useViewConfigSheet } from '../../../recoil/viewConfig'
import { buildConvertLeadFormModalProperties } from '../../../rsheet/components/modal/ConvertLeadFormModal'
import { buildSyncUserPlanNotFoundModalProperties } from './SyncUserPlanNotFoundModal'

// バーの中身 (件数 + ボタン) は固定高。Drawer bottom の slide は translateY(100%) (= 要素自身の高さ基準) のため、
// height を auto にすると高さ確定 (auto → 実高さ) の前後で slide 量が変わり、表示/非表示が 2 段階でカクつく。固定値で防ぐ
const SELECTION_BAR_HEIGHT_PX = 48

// 行選択中に画面下部へ表示する操作バー。pickedIds の写像として宣言的に render する (cf. issue #9165)
export const SheetSelectionBar = ({
  compiledConfig,
  view,
  ability,
  query,
}: {
  compiledConfig: CompiledSheetViewConfig
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  query: ViewQueryList
}): ReactNode => {
  const [pickedIds, setPickedIds] = useRecoilState(pickedIdsAtom)
  const sheet = useViewConfigSheet()
  const isSheetForLead = sheet.tree?.modelName === 'salesforce_lead' // XX: ベタ書きしている
  const { modal } = App.useApp()
  const { t } = useTranslation()

  // Drawer は閉じアニメ中も children を再 render するため (rc-dialog の MemoChildren 相当の凍結機構がない)、
  // 件数表示が「0 件を選択中」に変わってからスライドアウトしてしまう。最後の非ゼロ件数を保持して表示に使う
  const [lastPickedCount, setLastPickedCount] = useState(pickedIds.length)
  if (pickedIds.length > 0 && pickedIds.length !== lastPickedCount) {
    setLastPickedCount(pickedIds.length)
  }

  return (
    <Drawer
      open={pickedIds.length > 0}
      placement="bottom"
      height={SELECTION_BAR_HEIGHT_PX}
      mask={false}
      closable={false}
      // Esc で閉じない (移行前の notification は閉じ操作を持たなかったため、選択解除はチェックボックス側で行う)
      keyboard={false}
      // サイドメニューに被らないよう、body へ portal せずシート領域 (relative な親) 内に出す
      // (getContainer={false} のとき antd が root を position: absolute にする)。
      // overflow-hidden はスライドアニメ中のはみ出し (translateY(100%)) が親の overflow-y-auto に
      // スクロールバーを一時発生させ、中央寄せ位置が横にずれるのを防ぐため
      getContainer={false}
      rootClassName="overflow-hidden"
      classNames={{
        // 移行前の notification と同じカード風 (幅 400・下部中央・浮き) に寄せる。
        // 中央寄せを transform でなく margin で行っているのは、Drawer のスライドアニメが transform を使うため (競合する)。
        // bottom / padding は antd が同プロパティをより高い詳細度で当てているため ! で上書きする
        wrapper: '!bottom-6 mx-auto w-[400px]',
        content: 'rounded-lg',
        body: '!px-6 !py-2',
      }}
    >
      <div className="flex w-full items-center justify-between align-middle">
        <span className="text-sm text-gray-600">
          <span className="mr-1 text-lg font-bold text-black">{lastPickedCount}</span>
          {t(`件を選択中`)}
        </span>
        <Space>
          {isSheetForLead && (
            <Button
              type="text"
              icon={<PlayCircleOutlined />}
              style={{
                color: CONSTANT.colors.primaryColor,
              }}
              onClick={() => {
                const m = modal.confirm(
                  buildConvertLeadFormModalProperties({
                    query,
                    leadIds: pickedIds,
                    onFinish: () => {
                      setPickedIds([])
                      m.destroy()
                    },
                    compiledConfig,
                    view,
                  }),
                )
              }}
            >
              {t(`取引を開始`)}
            </Button>
          )}
          <DestroyButton compiledConfig={compiledConfig} view={view} ability={ability} query={query} />
        </Space>
      </div>
    </Drawer>
  )
}

const DestroyButton = ({
  compiledConfig,
  view,
  ability,
  query,
}: {
  compiledConfig: CompiledSheetViewConfig
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  query: ViewQueryList
}): ReactNode => {
  const { modal } = App.useApp()
  const { t } = useTranslation()
  const me = useMe()
  const [pickedIds] = useRecoilState(pickedIdsAtom)
  const savingFlag = useIsSavingState()
  const recordsAndChangesMutation = useRecordsAndChangesMutation(compiledConfig, view, query)
  const { existingRecordIds: existIds } = partitionPickedRecordIds(pickedIds)
  const syncEnabled = checkSyncEnabled(me.myUser)
  const { setIsChangedDrillDownModal } = useRecoilValue(viewsContextAtom)

  const deleteRecords = (isDeleteAllChildren?: boolean): void => {
    if (syncEnabled) {
      void recordsAndChangesMutation.destroyPickedRecords(isDeleteAllChildren) // confirmを早めに閉じるためにvoidにしている。エラーハンドリングをこのレイヤーでやっていないのでこれで問題ないはずだが、やや不安

      if (setIsChangedDrillDownModal !== undefined) {
        setIsChangedDrillDownModal(true)
      }
    } else {
      modal.confirm(buildSyncUserPlanNotFoundModalProperties())
    }
  }

  if ((!ability.canSaveRecord || !ability.canDeleteRecord) && !ability.canDeleteSalescoreObjectRecord) {
    return (
      <ButtonWithTooltip
        className="flex items-center gap-1"
        type="primary"
        shape="round"
        danger
        disabled
        tooltipTitle={t(`権限がないため、削除はできません。`)}
      >
        {t(`削除`)}
        <DownOutlined style={{ fontSize: 10 }} />
      </ButtonWithTooltip>
    )
  }

  if (existIds.length > 0) {
    return (
      <DropdownDeleteButton
        loading={savingFlag.isTrue}
        hasConfirm
        onDelete={() => {
          deleteRecords()
        }}
        onDeleteWithAllChildren={() => {
          deleteRecords(true)
        }}
        query={query}
      />
    )
  }

  return (
    <DropdownDeleteButton
      loading={savingFlag.isTrue}
      onDelete={() => {
        deleteRecords()
      }}
      onDeleteWithAllChildren={() => {
        deleteRecords(true)
      }}
      query={query}
    />
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function DropdownDeleteButton({
  loading,
  hasConfirm,
  onDelete,
  onDeleteWithAllChildren,
  query,
}: {
  loading: boolean
  hasConfirm?: boolean
  onDelete: () => void
  onDeleteWithAllChildren: () => void
  query: ViewQueryList
}): ReactNode {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  // 削除確定後は Dropdown を明示的に閉じる。Drawer は閉じても children を unmount しないため
  // (旧 notification 実装はアクション後に destroy され Dropdown ごと消えていた)、閉じないとメニューが残る
  const items: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <DropdownDeleteButtonMenu
          label={t('レコードの削除')}
          hasConfirm={hasConfirm}
          onDelete={() => {
            setOpen(false)
            onDelete()
          }}
        />
      ),
    },
    (query.tree.children ?? []).length > 0
      ? {
          key: '2',
          label: (
            <DropdownDeleteButtonMenu
              label={t('レコードの削除(子孫含む)')}
              hasConfirm={hasConfirm}
              onDelete={() => {
                setOpen(false)
                onDeleteWithAllChildren()
              }}
            />
          ),
        }
      : undefined,
  ].filter(isNotNullish)

  return (
    <Dropdown
      menu={{ items }}
      placement="top"
      trigger={['click']}
      popupRender={(item) => <div className="-translate-y-2">{item}</div>}
      open={open}
      onOpenChange={(open, info) => {
        if (info.source === 'trigger' || open) {
          setOpen(open)
        }
      }}
    >
      <Button className="flex items-center gap-1" danger type="primary" shape="round" loading={loading}>
        {t(`削除`)}
        <DownOutlined style={{ fontSize: 10 }} />
      </Button>
    </Dropdown>
  )
}

function DropdownDeleteButtonMenu({
  label,
  hasConfirm,
  onDelete,
}: {
  label: string
  hasConfirm?: boolean
  onDelete: () => void
}): ReactNode {
  const { t } = useTranslation()
  return hasConfirm === true ? (
    <Popconfirm
      onConfirm={onDelete}
      title={
        <div>
          {t(`本当に削除しますか？(この操作は取り消せません。`)}
          <br />
          {t(`また、変更中の項目はすべてクリアされます)`)}
        </div>
      }
      okText={t(`削除`)}
      cancelText={t(`キャンセル`)}
    >
      <span className="inline-block w-full text-red-500">{label}</span>
    </Popconfirm>
  ) : (
    <span className="inline-block w-full text-red-500" onClick={onDelete}>
      {label}
    </span>
  )
}
